import React, { useState } from "react";
import produce from "immer";
import isEqual from "lodash/isEqual";
import { injectIntl } from "react-intl";
import {
  Wrapper,
  Sections,
  Section,
  SectionLabel,
  SectionContent,
  SectionWrapper,
  InputGroup,
  Label,
  Input,
  Select,
  Checkbox,
  Buttons,
  Button,
  SliderWrapper,
  Slider,
  SliderInput,
} from "./styled";
const LabelValueSelector = React.lazy(() =>
  import("../../../../../../admin/src/components/ui/LabelValueSelector")
);

const recalculateWidth = (cols, colIndexToSkip) => {
  const totalColWidth = cols.reduce((acc, col) => acc + parseInt(col.width), 0);
  const MAX_WIDTH = 100;
  const differenceFromMax = MAX_WIDTH - totalColWidth;
  let remainderToDistribute = differenceFromMax % (cols.length - 1);
  const widthToDistribute =
    (differenceFromMax - remainderToDistribute) / (cols.length - 1);

  cols.forEach((col, index) => {
    if (index === colIndexToSkip) {
      return;
    }

    let widthToAdd = widthToDistribute;

    if (col.width > 1) {
      if (remainderToDistribute > 0) {
        widthToAdd++;
        remainderToDistribute--;
      } else if (remainderToDistribute < 0) {
        widthToAdd--;
        remainderToDistribute++;
      }
    }
    const newWidth = parseInt(col.width) + widthToAdd;

    // Column width should never be smaller than 1
    col.width = newWidth >= 1 ? newWidth : 1;
  });

  const newTotalColWidth = cols.reduce(
    (acc, col) => acc + parseInt(col.width),
    0
  );

  /**
   * Run recursively until total width equals 100 or break if
   * total column width has not changed this iteration to prevent infinite loops
   */
  if (newTotalColWidth !== 100 && newTotalColWidth !== totalColWidth) {
    recalculateWidth(cols, colIndexToSkip);
  }
};

const Col = ({
  data,
  colIdx,
  setCols,
  setSettingsDraft,
  intl,
  validations,
  setIsDndDisabled = () => {},
  cols,
}) => {
  const [values, setValues] = useState(data);

  const onChange = (key, value) => {
    setValues({ ...values, [key]: value });
  };

  const save = () => {
    setCols(
      produce((draft) => {
        draft[colIdx] = { ...data, ...values };
        recalculateWidth(draft, colIdx);
      })
    );

    setSettingsDraft(null);
  };

  const {
    title = "",
    thAlign = "left",
    tdAlign = "center",
    colors = "default",
    background = "",
    color = "",
    type = "number",
    format = "float",
    source = {},
    round = "",
    prefix = "",
    suffix = "",
    disabled = false,
    hidden = false,
    shrink = false,
    validation = {},
    width,
    required = false,
  } = values;

  return (
    <Wrapper>
      <Sections>
        <SectionWrapper>
          <Section>
            <SectionLabel>
              {intl.formatMessage({ id: "Col.Settings" })}
            </SectionLabel>
            <SectionContent>
              <InputGroup>
                <Label>
                  {intl.formatMessage({ id: "Col.Settings.title" })}
                </Label>
                <Input
                  value={title}
                  onChange={(e) => onChange("title", e.target.value)}
                />
              </InputGroup>
              <InputGroup>
                <Label>
                  {intl.formatMessage({ id: "Col.Settings.alignment" })}
                </Label>
                <Select
                  value={thAlign}
                  onChange={(e) => onChange("thAlign", e.target.value)}
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </Select>
              </InputGroup>
              <InputGroup>
                <Label>
                  {intl.formatMessage({ id: "Col.Settings.colors" })}
                </Label>
                <Select
                  value={colors}
                  onChange={(e) => onChange("colors", e.target.value)}
                >
                  <option value="default">Default</option>
                  <option value="custom">Custom</option>
                </Select>
              </InputGroup>
              {colors === "custom" && (
                <>
                  <InputGroup>
                    <Label>
                      {intl.formatMessage({
                        id: "Col.Settings.backgroundColor",
                      })}
                    </Label>
                    <Input
                      type="color"
                      value={background}
                      onChange={(e) => onChange("background", e.target.value)}
                    />
                  </InputGroup>
                  <InputGroup>
                    <Label>
                      {intl.formatMessage({ id: "Col.Settings.textColor" })}
                    </Label>
                    <Input
                      type="color"
                      value={color}
                      onChange={(e) => onChange("color", e.target.value)}
                    />
                  </InputGroup>
                </>
              )}
              <InputGroup>
                <Label htmlFor="validation" float>
                  {intl.formatMessage({ id: "Cell.Settings.validation" })}
                </Label>
                <Select
                  value={validation._id}
                  onChange={(e) => {
                    onChange(
                      "validation",
                      validations.find((v) => v._id === e.target.value)
                    );
                  }}
                >
                  <option value="">
                    {intl.formatMessage({
                      id: "Cell.Settings.noValidation",
                    })}
                  </option>
                  {validations.map((validation) => {
                    return (
                      <option value={validation._id} key={validation._id}>
                        {validation.name}
                      </option>
                    );
                  })}
                </Select>
              </InputGroup>
              <InputGroup>
                <Label>Storlek</Label>
                <SliderWrapper>
                  <Slider
                    type="range"
                    value={width}
                    onChange={(e) => onChange("width", e.target.value)}
                    max={100 - (cols.length - 1)}
                    min={1}
                    disabled={cols.length === 1}
                  />
                  <SliderInput
                    value={width}
                    onChange={(e) => onChange("width", e.target.value)}
                    disabled={cols.length === 1}
                  />
                </SliderWrapper>
              </InputGroup>
              <InputGroup>
                <Checkbox
                  id="required"
                  checked={required}
                  onChange={() => onChange("required", !required)}
                />
                <Label htmlFor="required" float>
                  Obligatorisk
                </Label>
              </InputGroup>
            </SectionContent>
          </Section>
        </SectionWrapper>
        <SectionWrapper>
          <Section>
            <SectionLabel>
              {intl.formatMessage({
                id: "Row.Settings",
              })}
            </SectionLabel>
            <SectionContent>
              <InputGroup>
                <Label>
                  {intl.formatMessage({
                    id: "Row.Settings.type",
                  })}
                </Label>
                <Select
                  value={type}
                  onChange={(e) => onChange("type", e.target.value)}
                >
                  <option value="text">Text</option>
                  <option value="number">
                    {intl.formatMessage({ id: "Number" })}
                  </option>
                  <option value="date">
                    {intl.formatMessage({ id: "Date" })}
                  </option>
                  <option value="time">
                    {intl.formatMessage({ id: "Time" })}
                  </option>
                  <option value="datetime-local">
                    {intl.formatMessage({ id: "Date and Time" })}
                  </option>
                  <option value="dropdown">Dropdown</option>
                </Select>
              </InputGroup>
              {type === "number" && (
                <InputGroup>
                  <Label>Format</Label>
                  <Select
                    value={format}
                    onChange={(e) => onChange("format", e.target.value)}
                  >
                    <option value="float">Float</option>
                    <option value="decimal">Decimal</option>
                    <option value="integer">Integer</option>
                  </Select>
                </InputGroup>
              )}
              {type === "number" && format === "integer" && (
                <InputGroup>
                  <Label>
                    {intl.formatMessage({
                      id: "Row.Settings.round",
                    })}
                  </Label>
                  <Select
                    value={round}
                    onChange={(e) => onChange("round", e.target.value)}
                  >
                    <option value="">
                      {intl.formatMessage({ id: "Cell.Settings.roundDefault" })}
                    </option>
                    <option value="up">
                      {intl.formatMessage({ id: "Cell.Settings.roundUp" })}
                    </option>
                    <option value="down">
                      {intl.formatMessage({ id: "Cell.Settings.roundDown" })}
                    </option>
                  </Select>
                </InputGroup>
              )}
              {type === "dropdown" && (
                <InputGroup className="dropdown-settings">
                  <Label>
                    {intl.formatMessage({
                      id: "Row.Settings.source",
                    })}
                  </Label>
                  <LabelValueSelector
                    name={"options"}
                    data={values.source || source}
                    value={(values.source || source)["options"]}
                    save={(updateObj) => {
                      if (
                        values.source &&
                        isEqual(values.source[updateObj.key], updateObj.value)
                      )
                        return;

                      onChange("source", {
                        ...values.source,
                        [updateObj.key]: updateObj.value,
                      });
                    }}
                  />
                </InputGroup>
              )}
              <InputGroup>
                <Label>
                  {intl.formatMessage({
                    id: "Row.Settings.alignment",
                  })}
                </Label>
                <Select
                  value={tdAlign}
                  onChange={(e) => onChange("tdAlign", e.target.value)}
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </Select>
              </InputGroup>
              <InputGroup>
                <Label>Prefix</Label>
                <Input
                  value={prefix}
                  onChange={(e) => onChange("prefix", e.target.value)}
                />
              </InputGroup>
              <InputGroup>
                <Label>Suffix</Label>
                <Input
                  value={suffix}
                  onChange={(e) => onChange("suffix", e.target.value)}
                />
              </InputGroup>
              <InputGroup>
                <Checkbox
                  id="disabled"
                  checked={disabled}
                  onChange={() => onChange("disabled", !disabled)}
                />
                <Label htmlFor="disabled" float>
                  {intl.formatMessage({
                    id: "Row.Settings.disabled",
                  })}
                </Label>
              </InputGroup>
              <InputGroup>
                <Checkbox
                  id="hidden"
                  checked={hidden}
                  onChange={() => onChange("hidden", !hidden)}
                />
                <Label htmlFor="hidden" float>
                  {intl.formatMessage({
                    id: "Row.Settings.hidden",
                  })}
                </Label>
                {hidden && (
                  <>
                    <Checkbox
                      id="shrink"
                      checked={shrink}
                      onChange={() => onChange("shrink", !shrink)}
                      style={{ marginLeft: "8px" }}
                    />
                    <Label htmlFor="shrink" float>
                      {intl.formatMessage({
                        id: "Row.Settings.shrink",
                      })}
                    </Label>
                  </>
                )}
              </InputGroup>
            </SectionContent>
          </Section>
        </SectionWrapper>
      </Sections>
      <Buttons>
        <Button
          onClick={() => {
            setIsDndDisabled(false);
            setSettingsDraft(null);
          }}
        >
          {intl.formatMessage({ id: "Col.Settings.cancel" })}
        </Button>
        <Button onClick={save}>
          {intl.formatMessage({ id: "Col.Settings.save" })}
        </Button>
      </Buttons>
    </Wrapper>
  );
};

export default injectIntl(Col);
